import React from 'react';
import {useDispatch} from 'react-redux';
import {IconButton, Menu, MenuItem} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import {Link} from 'react-router-dom';
import {ScrollingText} from '../../../../common/scrollingText/ScrollingText';
import {ProgressSlider} from '../controller/progressSlider/ProgressSlider';
import {SpinnerControl} from '../spinnerControl/SpinnerControl';
import {PlayerEventBus} from '../../playerEventBus';
import {usePlayerDispatch, usePlayerSelector} from '../../../../../store/slices/player/playerSlice';
import {updateShuffle} from '../../../../../store/slices/player/actions';
import {toggleLike} from '../../../../../store/slices/likedSongs/reducers';
import {useIsLiked} from '../../../../../store/slices/likedSongs/likedSongsSlice';
import {PlaylistPickerDialog} from '../../../../library/PlaylistPickerDialog';
import {CastButton} from '../../../../../native/cast/CastButton';
import styles from './FullScreenPlayer.module.scss';

interface FullScreenPlayerProps {
    onBack: () => void;
    getPlayerCurrentTime: () => number;
}

export const FullScreenPlayer: React.FC<FullScreenPlayerProps> = ({onBack, getPlayerCurrentTime}) => {
    const dispatch = useDispatch();
    const playerDispatch = usePlayerDispatch();
    const {nowPlaying, status, shuffle} = usePlayerSelector(['nowPlaying', 'status', 'shuffle']);
    const isLiked = useIsLiked(nowPlaying?.song?.id ?? '');
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
    const [pickerOpen, setPickerOpen] = React.useState(false);

    if (!nowPlaying) return null;

    const {song, playlist} = nowPlaying;
    const artImg = song.img || playlist?.img;

    const showPause = status === 'Playing';
    const showBuffering = status === 'Buffering';
    const showPlay = !showPause && !showBuffering;

    const stop = (e: React.MouseEvent) => e.stopPropagation();
    const handlePlay = (e: React.MouseEvent) => { stop(e); PlayerEventBus.emit('play'); };
    const handlePause = (e: React.MouseEvent) => { stop(e); PlayerEventBus.emit('pause'); };
    const handleNext = (e: React.MouseEvent) => { stop(e); PlayerEventBus.emit('next'); };
    const handlePrev = (e: React.MouseEvent) => { stop(e); PlayerEventBus.emit('prev'); };
    const handleShuffle = (e: React.MouseEvent) => { stop(e); playerDispatch(updateShuffle(!shuffle)); };
    const handleBack = (e: React.MouseEvent) => { stop(e); onBack(); };
    const handleLike = (e: React.MouseEvent) => { stop(e); if (song) dispatch(toggleLike(song)); };

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); };
    const handleMenuClose = () => setMenuAnchor(null);
    const handleAddToPlaylist = () => { setMenuAnchor(null); setPickerOpen(true); };

    return (
        <div className={styles.fsPlayer} onClick={stop}>
            <div className={styles.fsPlayer__header}>
                <IconButton aria-label="Close" onClick={handleBack}>
                    <KeyboardArrowDownIcon sx={{fontSize: 28, color: 'white'}}/>
                </IconButton>
                <div className={styles.fsPlayer__headerTitle}>NOW PLAYING</div>
                <CastButton size={22}/>
                <IconButton aria-label="More options" onClick={handleMenuOpen}>
                    <MoreVertIcon sx={{color: 'white'}}/>
                </IconButton>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={handleMenuClose}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                >
                    <MenuItem onClick={handleAddToPlaylist}>Add to Playlist</MenuItem>
                </Menu>
            </div>

            <div className={styles.fsPlayer__artWrap}>
                <div className={styles.fsPlayer__artGlow}/>
                <div className={styles.fsPlayer__artFrame}>
                    {artImg
                        ? <img src={artImg} alt={song.name} className={styles.fsPlayer__art}/>
                        : <MusicNoteIcon sx={{fontSize: 120, color: 'rgba(255,255,255,0.25)'}}/>}
                </div>
            </div>

            <div className={styles.fsPlayer__info}>
                <div className={styles.fsPlayer__textBlock}>
                    <ScrollingText
                        text={song.name}
                        className={styles.fsPlayer__title}
                    />
                    <div className={styles.fsPlayer__subtitle}>
                        <span>{song.artist}</span>
                        {playlist?.name && <>
                            <span className={styles.fsPlayer__dot}>•</span>
                            <span>{playlist.name}</span>
                        </>}
                    </div>
                </div>
                <IconButton
                    aria-label={isLiked ? 'Remove from Liked Songs' : 'Add to Liked Songs'}
                    onClick={handleLike}
                    className={`${styles.fsPlayer__like} ${isLiked ? styles.fsPlayer__likeOn : ''}`}
                >
                    {isLiked
                        ? <FavoriteIcon sx={{fontSize: 22}}/>
                        : <FavoriteBorderIcon sx={{fontSize: 22}}/>}
                </IconButton>
            </div>

            <div className={styles.fsPlayer__progress}>
                <ProgressSlider
                    getPlayerCurrentTime={getPlayerCurrentTime}
                    isFullScreen
                />
            </div>

            <div className={styles.fsPlayer__controls}>
                <IconButton aria-label="Shuffle" onClick={handleShuffle}>
                    <ShuffleIcon
                        sx={{fontSize: 22, color: shuffle ? '#f4d160' : 'rgba(255,255,255,0.75)'}}
                    />
                </IconButton>
                <IconButton aria-label="Previous" onClick={handlePrev}>
                    <SkipPreviousIcon sx={{fontSize: 40, color: 'white'}}/>
                </IconButton>
                <IconButton
                    className={styles.fsPlayer__playButton}
                    aria-label={showPause ? 'Pause' : 'Play'}
                    onClick={showPause ? handlePause : handlePlay}
                >
                    {showBuffering && <SpinnerControl isFullScreen={false}/>}
                    {showPause && <PauseIcon sx={{fontSize: 38, color: 'white'}}/>}
                    {showPlay && <PlayArrowIcon sx={{fontSize: 42, color: 'white', marginLeft: '2px'}}/>}
                </IconButton>
                <IconButton aria-label="Next" onClick={handleNext}>
                    <SkipNextIcon sx={{fontSize: 40, color: 'white'}}/>
                </IconButton>
                <Link to={`/playlists/${playlist.id}`} onClick={stop}>
                    <IconButton aria-label="Queue">
                        <QueueMusicIcon sx={{fontSize: 22, color: 'rgba(255,255,255,0.75)'}}/>
                    </IconButton>
                </Link>
            </div>

            <PlaylistPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                song={song}
                sourceImg={playlist?.img}
            />
        </div>
    );
};
